import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, ButtonBase, makeStyles, Popover } from '@material-ui/core';
import moment from 'moment';
import _ from 'lodash';
import { useSelector } from 'react-redux';
import { AddIcon, CloseIcon, DropLeftIcon, DropRightIcon, SettingIcon, TimeIcon } from '../../../../components/Icon';
import StyleButton from '../../../../components/StyleButton';
import { teleVisitAPI } from '../../../../apis';
import { i18n } from '../../../../language';
import { authNameE } from '../../../../utils/userFuncs';
import ServiceHoursSetting from './Popup/ServiceHoursSetting';
import AddServiceHours from './Popup/AddServiceHours';
import EditServiceHours from './Popup/EditServiceHours';
const useStyles = makeStyles(() => ({
  head: {
    display: 'flex',
    flexBasis: '100%',
    justifyContent: 'center',
    fontFamily: 'NotoSans-Bold',
    fontSize: '1rem',
    color: '#2e4469',
    minWidth: '10.375rem',
  },
  title: {
    fontFamily: 'NotoSans-Bold',
    fontSize: '1.5rem',
    color: '#333333',
    marginRight: '1.25rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  displayTime: {
    fontFamily: 'NotoSans-Bold',
    fontSize: '1.125rem',
    color: '#2e4469',
    marginRight: '1.25rem',
    width: '10rem',
    textAlign: 'center',
  },
  description: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '0.75rem',
    color: '#aaaaaa',
  },
  cardRoot: ({ index, weekIndex, addCtrl }) => ({
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    minWidth: '10.375rem',
    height: '6.5rem',
    flexBasis: '100%',
    border: 'solid 1px #d7e3f1',
    translate: `-${index}px -${weekIndex}px`,
    padding: '0.5rem',
    '&>div': {
      opacity: addCtrl ? '1' : '0.3',
    },
    '&>button': {
      opacity: addCtrl ? '1' : '0.3',
    },
  }),
  date: ({ flag, date }) => ({
    display: 'flex',
    justifyContent: 'flex-end',
    width: '100%',
    fontFamily: 'NotoSans-Bold',
    fontSize: '1rem',
    color: moment(date, 'YYYY-MM-DD').isSame(moment(), 'd') ? '#538fff' : '#2e4469',
    opacity: flag ? 1 : 0.3,
  }),
  period: ({ bgColor }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'start',
    width: '100%',
    height: '2rem',
    backgroundColor: '#bad2ff',
    padding: '0.5rem 1rem',
    position: 'relative',
    overflow: 'hidden',
    borderRadius: '5px',
    fontFamily: 'NotoSans-Medium',
    fontSize: '0.75rem',
    color: '#32579e',
    '&::before': {
      position: 'absolute',
      top: '0',
      left: '0',
      content: '""',
      display: 'block',
      width: '0.375rem',
      height: '100%',
      backgroundColor: bgColor,
      zIndex: '9',
    },
  }),
  addPeriod: {
    borderRadius: '5px',
    border: '1px dashed #7d86a9',
    padding: '0.5rem',
    width: '100%',
    height: '2rem',
    fontFamily: 'NotoSans-Medium',
    fontSize: '0.75rem',
    lineHeight: '1rem',
    color: '#7d86a9',
  },
  addIcon: {
    fontSize: '0.656rem',
    color: '#7d86a9',
    marginRight: '0.484rem',
  },
  icon: {
    fontSize: '0.813rem',
    marginRight: '0.25rem',
    color: '#538fff',
  },
  circle: {
    width: '0.25rem',
    minWidth: '0.25rem',
    minHeight: '0.25rem',
    height: '0.25rem',
    borderRadius: '50%',
    backgroundColor: '#999999',
    marginRight: '0.375rem',
    translate: '0 50%',
  },
  more: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    fontFamily: 'NotoSans-Medium',
    fontSize: '0.75rem',
    color: '#999999',
  },
  morePopup: () => {
    const card = document.getElementById('card');
    return {
      '& .MuiPopover-paper': {
        border: 'solid 1px #d7e3f1',
        boxShadow: '0 1px 4px 0 rgba(0, 0, 0, 0.26)',
        borderRadius: '10px',
        padding: '0.5rem',
        width: card?.offsetWidth ?? 'unset',
      },
    };
  },
  moreCloseBtn: {
    width: '100%',
    height: '1.891rem',
    marginTop: '0.438rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  moreCloseIcon: {
    fontSize: '1.375rem',
    color: '#538fff',
  },
  smallAddIcon: {
    fontSize: '0.875rem',
  },
}));

const ServiceHours = () => {
  const classes = useStyles();
  const roleMap = useSelector((state) => state.roleMap);
  const [time, setTime] = useState(moment());
  const [settingDialog, setSettingDialog] = useState(false);
  const firstWeekend = moment(moment(time).startOf('month')).startOf('week');
  const endWeekend = moment(moment(time).endOf('month')).endOf('week');
  const timeHandler = useCallback(
    (type) => () => {
      switch (type) {
        case 'today':
          setTime(moment());
          break;
        case 'previous':
          setTime((preTime) => moment(preTime).add(-1, 'month'));
          break;
        case 'next':
          setTime((preTime) => moment(preTime).add(1, 'month'));
          break;
        default:
          break;
      }
    },
    []
  );

  const settingHandler = useCallback(
    (props) => () => {
      setSettingDialog(props);
    },
    []
  );
  const [list, setList] = useState([]);
  // getServiceHoursList api
  const serviceHoursListApi = useCallback(async () => {
    try {
      const { result } = await teleVisitAPI.getServiceHoursList({
        startDay: moment(firstWeekend).valueOf(),
        endDay: moment(endWeekend).valueOf(),
      });

      setList(
        _.map(result, (item) => ({
          ...item,
          date: moment(item.date).format('yyyy-MM-DD'),
        }))
      );
    } catch (error) {}
  }, [time]);
  // Add Service Hours
  const addServiceHoursApi = useCallback(
    async (data, onClose) => {
      try {
        await teleVisitAPI.postAddServiceHours({
          ...data,
          startDay: moment(data.startDay).valueOf(),
          endDay: moment(data.endDay).valueOf(),
        });
        onClose();
        await serviceHoursListApi();
      } catch (error) {}
    },
    [serviceHoursListApi]
  );
  // update Service Hours
  const updateServiceHoursApi = useCallback(
    async (data, onClose) => {
      try {
        await teleVisitAPI.postUpdateServiceHours({
          ...data,
          date: moment(data.date).valueOf(),
          startDay: moment(data.startDay).valueOf(),
          endDay: moment(data.endDay).valueOf(),
        });
        onClose();
        await serviceHoursListApi();
      } catch (error) {
        console.log(error, 'updateServiceHoursApi');
      }
    },
    [serviceHoursListApi]
  );
  // remove Service Hours
  const removeServiceHoursApi = useCallback(
    async (data, onClose) => {
      try {
        await teleVisitAPI.postRemoveServiceHours({
          ...data,
          date: moment(data.date).valueOf(),
          startDay: moment(data.startDay).valueOf(),
          endDay: moment(data.endDay).valueOf(),
        });
        onClose();
        await serviceHoursListApi();
      } catch (error) {}
    },
    [serviceHoursListApi]
  );
  const [settingData, setSettingData] = useState(null);

  // get quota setting
  const quotaSettingApi = useCallback(async () => {
    try {
      const { result } = await teleVisitAPI.getQuotaSetting();
      if (!Object.values(result).some((item) => item !== null)) {
        if (roleMap[authNameE.setting_televisit_setup_patient_quota]) {
          setSettingDialog(true);
        }
      }
      setSettingData(result);
    } catch (error) {}
  }, []);

  // add/update setting
  const saveSetting = useCallback(async (data, onClose) => {
    try {
      await teleVisitAPI.postAddUpdateQuotaSetting(data);
      onClose();
      (async () => {
        await quotaSettingApi();
      })();
      (async () => {
        await serviceHoursListApi();
      })();
    } catch (error) {}
  }, []);

  useEffect(() => {
    (async () => {
      await serviceHoursListApi();
    })();
  }, [serviceHoursListApi]);

  useEffect(() => {
    if (
      roleMap[authNameE.setting_televisit_setup_patient_quota] ||
      roleMap[authNameE.setting_televisit_create_update_service_hours]
    ) {
      (async () => {
        await quotaSettingApi();
      })();
    }
  }, [quotaSettingApi]);

  return (
    <>
      <Box pt="1.25rem">
        <Box display="flex" justifyContent="space-between" mb="0.438rem">
          <Box display="flex">
            <div className={classes.title}>{i18n.t('TELEVISIT_SETTING_SERVICE_HOURS')}</div>
            <StyleButton
              text={i18n.t('TELEVISIT_SETTING_TODAY')}
              color="#333333"
              width="5.875rem"
              height="3rem"
              m="0 1.25rem 0 0"
              backgroundColor="#fff"
              border="solid 1.3px #e4e7eb"
              borderRadius="10.7px"
              onClick={timeHandler('today')}
            />
            <Box display="flex" alignItems="center" justifyContent="center">
              <StyleButton
                Icon={DropLeftIcon}
                width="3rem"
                height="3rem"
                backgroundColor="#fff"
                iconStyle={{ fontSize: '0.75rem' }}
                m="0 1.25rem 0 0"
                border="solid 1.3px #e4e7eb"
                borderRadius="10.7px"
                onClick={timeHandler('previous')}
              />
              <div className={classes.displayTime}>{moment(time).format('MMMM, YYYY')}</div>
              <StyleButton
                Icon={DropRightIcon}
                width="3rem"
                height="3rem"
                backgroundColor="#fff"
                iconStyle={{ fontSize: '0.75rem' }}
                m="0 1.25rem 0 0"
                border="solid 1.3px #e4e7eb"
                borderRadius="10.7px"
                onClick={timeHandler('next')}
              />
            </Box>
          </Box>
          {roleMap[authNameE.setting_televisit_setup_patient_quota] && (
            <StyleButton
              width="10.375rem"
              height="3rem"
              backgroundColor="#fff"
              text={i18n.t('TELEVISIT_SETTING_BUTTON')}
              Icon={SettingIcon}
              border="solid 1.3px #538fff"
              onClick={settingHandler(true)}
            />
          )}
        </Box>
        {roleMap[authNameE.setting_televisit_create_update_service_hours] && (
          <div className={classes.description}>{i18n.t('TELEVISIT_SETTING_DESC')}</div>
        )}
        <TimeslotTable
          date={time}
          list={list}
          addServiceHoursApi={addServiceHoursApi}
          updateServiceHoursApi={updateServiceHoursApi}
          removeServiceHoursApi={removeServiceHoursApi}
          firstWeekend={firstWeekend}
          endWeekend={endWeekend}
          settingData={settingData}
          setSettingDialog={setSettingDialog}
        />
      </Box>
      {settingDialog && (
        <ServiceHoursSetting onClose={settingHandler(false)} saveSetting={saveSetting} settingData={settingData} />
      )}
    </>
  );
};

const TimeslotTable = ({
  date = moment(),
  list,
  addServiceHoursApi,
  firstWeekend,
  endWeekend,
  updateServiceHoursApi,
  removeServiceHoursApi,
  settingData,
  setSettingDialog,
}) => {
  const classes = useStyles();
  const language = useSelector((state) => state.settings.language);
  const weekday = useMemo(
    () => [
      i18n.t('TELEVISIT_SETTING_SUN'),
      i18n.t('TELEVISIT_SETTING_MON'),
      i18n.t('TELEVISIT_SETTING_TUE'),
      i18n.t('TELEVISIT_SETTING_WED'),
      i18n.t('TELEVISIT_SETTING_THU'),
      i18n.t('TELEVISIT_SETTING_FRI'),
      i18n.t('TELEVISIT_SETTING_SAT'),
    ],
    [language]
  );
  const calendarData = useMemo(() => {
    let calendar = [[]];
    let index = 0;
    while (
      moment(firstWeekend)
        .weekday(index * 7)
        .isSameOrBefore(endWeekend)
    ) {
      let week = [];
      let weekIndex = 0;
      while (weekIndex < 7) {
        let day = moment(firstWeekend)
          .weekday(index * 7 + weekIndex)
          .format('YYYY-MM-DD');

        let period = [];
        if (weekIndex === 2) {
          period = [{ time: '15:00 - 18:00' }, { time: '19:00 - 20:00' }, { time: '22:00 - 22:30' }];
        } else if (index === 2) {
          period = [{ time: '16:00 - 18:00' }];
        }

        week[weekIndex] = {
          date: day,
          flag: moment(date).month() === moment(day).month(),
          period,
        };
        weekIndex++;
      }
      calendar[index] = week;
      index++;
    }

    calendar = _.map(calendar, (weekItem) =>
      _.map(weekItem, (item) => ({
        ...item,
        period:
          _.find(list, (listItem) => listItem.date === item.date)?.serviceHours?.map((serviceItem) => ({
            ...serviceItem,
            time: `${serviceItem.startTime} - ${serviceItem.endTime}`,
          })) ?? [],
      }))
    );

    return calendar;
  }, [date, list]);

  return (
    <Box mt="1.25rem" overflow="auto">
      <Box display="flex">
        {weekday.map((item, index) => (
          <Box className={classes.head} style={{ translate: `-${index}px` }} key={item}>
            {item}
          </Box>
        ))}
      </Box>
      {calendarData.map((weekItem, weekIndex) => (
        <Box display="flex" key={weekItem[0].date}>
          {weekItem.map((item, index) => (
            <Card
              {...item}
              index={index}
              weekIndex={weekIndex}
              key={item.date}
              addServiceHoursApi={addServiceHoursApi}
              updateServiceHoursApi={updateServiceHoursApi}
              removeServiceHoursApi={removeServiceHoursApi}
              settingData={settingData}
              setSettingDialog={setSettingDialog}
            />
          ))}
        </Box>
      ))}
    </Box>
  );
};
const CardBtn = ({ item, editHandler, blue, yellow, green, purple, magenta }) => {
  const [beforeBgColor, setBeforeBgColor] = useState('');
  const [colorVal, setColorVal] = useState({});
  const classes = useStyles({ bgColor: beforeBgColor });

  useEffect(() => {
    switch (item.serviceHoursColor) {
      case '#BAD2FF':
        setColorVal(blue);
        setBeforeBgColor(blue.beforeColor);
        break;
      case '#FBEFB7':
        setColorVal(yellow);
        setBeforeBgColor(yellow.beforeColor);
        break;
      case '#A9EDDD':
        setColorVal(green);
        setBeforeBgColor(green.beforeColor);
        break;
      case '#DECFFF':
        setColorVal(purple);
        setBeforeBgColor(purple.beforeColor);
        break;
      case '#FFD8E3':
        setColorVal(magenta);
        setBeforeBgColor(magenta.beforeColor);
        break;
      default:
        return '';
    }
  }, [beforeBgColor, item.serviceHoursColor]);

  return (
    <ButtonBase
      className={classes.period}
      style={{ backgroundColor: colorVal.bgColor, color: colorVal.fontColor }}
      onClick={editHandler}
    >
      <TimeIcon className={classes.icon} style={{ color: colorVal.fontColor }} /> {item.time}
    </ButtonBase>
  );
};
const CardButton = ({ period, editHandler, blue, yellow, green, purple, magenta }) => {
  const [beforeBgColor, setBeforeBgColor] = useState('');
  const [colorVal, setColorVal] = useState({});
  const classes = useStyles({ bgColor: beforeBgColor });
  useEffect(() => {
    switch (period?.serviceHoursColor) {
      case '#BAD2FF':
        setColorVal(blue);
        setBeforeBgColor(blue.beforeColor);
        break;
      case '#FBEFB7':
        setColorVal(yellow);
        setBeforeBgColor(yellow.beforeColor);
        break;
      case '#A9EDDD':
        setColorVal(green);
        setBeforeBgColor(green.beforeColor);
        break;
      case '#DECFFF':
        setColorVal(purple);
        setBeforeBgColor(purple.beforeColor);
        break;
      case '#FFD8E3':
        setColorVal(magenta);
        setBeforeBgColor(magenta.beforeColor);
        break;
      default:
        return '';
    }
  }, [period]);
  return (
    <ButtonBase
      className={classes.period}
      style={{ backgroundColor: colorVal.bgColor, color: colorVal.fontColor }}
      onClick={editHandler(true)(period)}
    >
      <TimeIcon className={classes.icon} style={{ color: colorVal.fontColor }} /> {period.time}
    </ButtonBase>
  );
};
const Card = ({
  index,
  weekIndex,
  date,
  flag,
  period,
  addServiceHoursApi,
  updateServiceHoursApi,
  removeServiceHoursApi,
  settingData,
  setSettingDialog,
}) => {
  const addCtrl = useMemo(() => moment(date, 'YYYY-MM-DD').isSameOrAfter(moment(), 'd'), [date]);
  const classes = useStyles({ index, weekIndex, flag, date, addCtrl });
  const [open, setOpen] = useState(false);
  const cardRef = useRef(null);
  const [addDialog, setAddDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const roleMap = useSelector((state) => state.roleMap);
  const [serviceHoursSettingId, setServiceHoursSettingId] = useState(null);
  let blue = { bgColor: '#bad2ff', beforeColor: '#538fff', fontColor: '#32579e' };
  let yellow = { bgColor: '#fbefb7', beforeColor: '#feac0d', fontColor: '#feac0d' };
  let green = { bgColor: '#a9eddd', beforeColor: '#00ac84', fontColor: '#00ac84' };
  let purple = { bgColor: '#dfcfff', beforeColor: '#6c2af8', fontColor: '#6c2af8' };
  let magenta = { bgColor: '#ffd8e3', beforeColor: '#ff50a4', fontColor: '#ff50a4' };

  const moreHandler = useCallback(() => {
    setOpen(true);
  }, [cardRef]);
  // add Service Hours
  const addHandler = useCallback(
    (props) => () => {
      if (roleMap[authNameE.setting_televisit_create_update_service_hours]) {
        if (settingData?.dailyQuota && settingData?.numOfDaysRelease && settingData?.weeklyQuota) {
          setAddDialog(props);
        } else if (roleMap[authNameE.setting_televisit_setup_patient_quota]) {
          setSettingDialog(true);
        }
      } else if (
        !settingData?.dailyQuota &&
        !settingData?.numOfDaysRelease &&
        !settingData?.weeklyQuota &&
        roleMap[authNameE.setting_televisit_setup_patient_quota]
      ) {
        setSettingDialog(true);
      }
    },
    [settingData, roleMap]
  );
  // edit Service Hours
  const editHandler = useCallback(
    (props) => (item) => (event) => {
      event?.stopPropagation();
      if (roleMap[authNameE.setting_televisit_create_update_service_hours]) {
        if (settingData?.dailyQuota && settingData?.numOfDaysRelease && settingData?.weeklyQuota) {
          setEditDialog(props);
          setServiceHoursSettingId(item?.serviceHoursSettingId);
        } else if (roleMap[authNameE.setting_televisit_setup_patient_quota]) {
          setSettingDialog(true);
        }
      } else if (
        !settingData?.dailyQuota &&
        !settingData?.numOfDaysRelease &&
        !settingData?.weeklyQuota &&
        roleMap[authNameE.setting_televisit_setup_patient_quota]
      ) {
        setSettingDialog(true);
      }
    },
    [settingData]
  );

  return (
    <>
      <div id="card" className={classes.cardRoot} ref={cardRef}>
        <div className={classes.date}>{moment(date, 'YYYY-MM-DD').date()}</div>
        {flag && period.length > 0 && (
          <>
            <CardButton
              period={period[0]}
              editHandler={editHandler}
              blue={blue}
              yellow={yellow}
              green={green}
              purple={purple}
              magenta={magenta}
            />
            <Box display="flex" justifyContent="space-between" mt="0.5rem">
              {period.length > 1 ? (
                <ButtonBase className={classes.more} onClick={moreHandler}>
                  <span className={classes.circle} /> {`${period.length - 1} ${i18n.t('TELEVISIT_SETTING_MORE')}...`}
                </ButtonBase>
              ) : (
                <div />
              )}
              {addCtrl && roleMap[authNameE.setting_televisit_create_update_service_hours] && (
                <ButtonBase className={classes.more} onClick={addHandler(true)}>
                  <AddIcon className={classes.smallAddIcon} />
                </ButtonBase>
              )}
            </Box>
          </>
        )}
        {flag && period.length === 0 && addCtrl && roleMap[authNameE.setting_televisit_create_update_service_hours] && (
          <ButtonBase className={classes.addPeriod} onClick={addHandler(true)}>
            <AddIcon className={classes.addIcon} /> {i18n.t('TELEVISIT_SETTING_SERVICE_HOURS')}
          </ButtonBase>
        )}
      </div>
      <Popover
        className={classes.morePopup}
        open={open}
        anchorEl={cardRef?.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <div className={classes.date}>{moment(date, 'YYYY-MM-DD').date()}</div>
        <Box display="flex" flexDirection="column" gridRowGap="0.75rem">
          {period.map((item, index) => (
            <CardBtn
              item={item}
              key={item.serviceHoursSettingId}
              editHandler={editHandler(true)(item)}
              blue={blue}
              yellow={yellow}
              green={green}
              purple={purple}
              magenta={magenta}
            />

            // <ButtonBase className={classes.period} key={index} style={{ backgroundColor: item.serviceHoursColor }}>
            //   <TimeIcon className={classes.icon} /> {item.time}
            // </ButtonBase>
          ))}
          {roleMap[authNameE.setting_televisit_create_update_service_hours] && addCtrl && (
            <ButtonBase className={classes.addPeriod} onClick={addHandler(true)}>
              <AddIcon className={classes.addIcon} /> {i18n.t('SETTING_TELE_TAB_SERVICE_HOURS')}
            </ButtonBase>
          )}
        </Box>
        <ButtonBase className={classes.moreCloseBtn} onClick={() => setOpen(false)}>
          <CloseIcon className={classes.moreCloseIcon} />
        </ButtonBase>
      </Popover>
      {addDialog && <AddServiceHours date={date} onClose={addHandler(false)} addServiceHoursApi={addServiceHoursApi} />}
      {editDialog && (
        <EditServiceHours
          onClose={editHandler(false)()}
          serviceHoursSettingId={serviceHoursSettingId}
          date={moment(date).valueOf()}
          updateServiceHoursApi={updateServiceHoursApi}
          removeServiceHoursApi={removeServiceHoursApi}
        />
      )}
    </>
  );
};

export default ServiceHours;
